// src/lib/gtag.ts
/**
 * @fileoverview Google Analytics (GA4) client utilities.
 * @description Provides a small wrapper around gtag for consistent event naming, timing helpers
 * (total vs. delta engagement), and high-level resource event helpers.
 *
 * Key concepts:
 * - sendEvent: thin wrapper over window.gtag('event', ...)
 * - createEngagementTimer: returns total ms since creation (for complete events)
 * - createEngagementStopwatch: returns ms since last call (for interact/input events)
 * - createEngagementTimers: convenient tuple [getTotalMs, getDeltaMs]
 * - sendResourceEvent: emits resource_* events with standard params (type, slug, etc.)
 * - Vercel Analytics: mapped GA actions also call `track()` so the Vercel dashboard shows custom events.
 *
 * Differences vs prior implementation:
 * - Previously all resource events were sent as a single 'resource_event' with a resource_action param.
 *   This version emits explicit event names: 'resource_view', 'resource_interact', 'resource_complete'.
 *   This improves report readability and filtering without relying on custom breakdowns.
 * - Timers are now clearly separated for total vs delta, enabling accurate per-interaction timing.
 */
// import { env } from "~/env"
import { track as vercelTrack } from '@vercel/analytics'

import { currentNavigationReferrerHost } from "~/lib/analytics/navigation-referrer"
import { normalizeAnalyticsUrl } from "~/lib/analytics/normalize-analytics-url"
import { SENSITIVE_QUERY_PARAM_RE } from "~/lib/analytics/sensitive-query-params"
import { sectionForPath } from "~/lib/analytics/attribution"
import { getClientContextProps } from "~/lib/analytics/client-context"
import { isAnalyticsAllowed } from "~/lib/analytics/consent"
import { isAnalyticsStorageAllowedAsync } from "~/lib/analytics/consent-storage-async"
import {
  getHydratedConsentRequiredSignal,
  getHydratedRegionResolved,
} from "~/lib/analytics/hydrate-region-cookie"
import {
  currentRumConsentEpoch,
  hasRegionCookieClient,
  isRumConsentGranted,
} from "~/lib/analytics/rum-consent"
import {
  FORWARDED_EVENT_PROP_KEYS,
  RUM_VIEW_INSTANCE_KEY,
} from "~/lib/rum/types"
import {
  readActiveExperimentDimensions,
  readFreshLedgerIngestToken,
  resolveConversionTargets,
} from "~/lib/experiments/client-dimensions"
import { LEDGER_INGEST_TOKEN_HEADER } from "~/lib/experiments/ledger-ingest-client"
import { CONVERSION_METRICS } from "~/lib/experiments/metrics"
import { intentForPath } from "~/lib/site-intelligence/intent"
import {
  isHighIntentActionLabel,
  normalizeActionLabel,
  surfaceFromLocation,
  type SiteActionKind,
  type SiteActionLabel,
  type SiteActionSurface,
} from "~/lib/site-intelligence/actions"
import { engagementSignalFor } from "~/lib/site-intelligence/engagement-signals"
import { peekJourneyProps, recordJourneyPage } from "~/lib/site-intelligence/session-journey"
import {
  getSessionEngagementProps,
  getSessionEngagementSummary,
  hasSessionEngagementBeenEmitted,
  markSessionEngagementEmitted,
  recordSiteAction,
  recordEngagementSignal,
  updateSessionMaxScroll,
} from "~/lib/site-intelligence/session-engagement"
import {
  categoryFor,
  isRegisteredEvent,
  vercelEventFor,
  type AnalyticsEventName,
  type AnalyticsEventParams,
  type EventPropValue,
} from "~/lib/analytics/registry"

// Re-exported so the privacy UI / consumers have a single import surface.
export { invalidateConsentCache, notifyConsentChanged } from "~/lib/analytics/consent"

// GA4 Measurement ID — single source of truth in ga-config (shared with the
// server layout's Consent Mode bootstrap), re-exported here for the many
// existing importers of `~/lib/gtag`.
export { GA_MEASUREMENT_ID } from "~/lib/analytics/ga-config"
import { GA_MEASUREMENT_ID } from "~/lib/analytics/ga-config"

/**
 * RUM drain event sink — build-time gated, lazily bound (r3542898935).
 * `gtag.ts` sits in the main analytics bundle, so a static import of the drain
 * would ship it (and keep a per-event no-op call) even for the documented
 * disabled default. The NEXT_PUBLIC_RUM_ENABLED literal is inlined at build
 * time (repo rule: literal `process.env.NEXT_PUBLIC_*` in client code), so
 * with the flag unset/false the whole branch — including the `import()` — is
 * dead code: no chunk, no module evaluation, and `sendEvent` calls a null
 * sink. Enabled builds bind the sink asynchronously; events fired before the
 * (cached, one-time) chunk resolves skip the drain — the drain is a sampled
 * telemetry sink, not a ledger, and its own buffers/consent gates apply from
 * the first bound call. Mirrors the layout's statically-eliminated
 * RumReporter gate.
 */
type RumEventSink = (name: string, eventParams: Record<string, unknown>) => void
let rumEventSink: RumEventSink | null = null
/**
 * Pre-bind queue (r3543138824): `resource_view` — the funnel DENOMINATOR —
 * fires on mount, typically BEFORE the drain chunk resolves, so dropping
 * pre-bind events would systematically undercount cold-load views. Events
 * fired before the sink binds are held here (bounded, oldest evicted) and
 * replayed through the drain's own enqueue once it loads — so the drain's
 * consent/region gates still evaluate every replayed event, and its flush
 * machinery applies unchanged. Enabled builds only: the disabled build's
 * whole branch (queue writes included) is dead code behind the inlined flag.
 *
 * Each queued entry carries the CONSENT EPOCH it was captured under
 * (r3543582090 — `~/lib/analytics/rum-consent`, a main-bundle module shared
 * with the drain precisely because this queue exists while the drain chunk
 * does not). This queue captures UNCONDITIONALLY, so without the epoch a
 * consent invalidation in the pre-bind window — an opt-in arriving in a
 * consent-required region, a withdrawal, a consent-required region
 * resolution — would leave pre-invalidation events here to replay (and pass
 * the drain's gate) under the LATER consent state. Replay drops any entry
 * whose epoch is no longer current.
 */
const pendingRumEvents: Array<{
  name: string
  params: Record<string, unknown>
  epoch: number
}> = []
/** Mirrors the drain's own MAX_BUFFER — the chunk resolves in well under the
 *  time it takes to emit 100 events, so eviction is a pathological-case cap. */
const MAX_PENDING_RUM_EVENTS = 100
if (process.env.NEXT_PUBLIC_RUM_ENABLED === "true") {
  void import("~/lib/analytics/rum-drain")
    .then((m) => {
      rumEventSink = m.enqueueRumEvent
      // Replay in emission order; each replayed event passes through the
      // drain's enqueue gate (consent, region) as if it had just fired —
      // except entries captured under a since-invalidated consent epoch,
      // which are dropped (never replayed, never transmitted — r3543582090).
      const epochNow = currentRumConsentEpoch()
      for (const pending of pendingRumEvents.splice(0)) {
        if (pending.epoch !== epochNow) continue
        try {
          m.enqueueRumEvent(pending.name, pending.params)
        } catch {
          // Analytics must never break UX.
        }
      }
    })
    .catch(() => {
      // Drain chunk unavailable — drop the queue; analytics must never break UX.
      pendingRumEvents.length = 0
    })

  /**
   * EARLY pagehide flush for the pre-bind queue (r3547669004): a fast bounce
   * can end the page BEFORE the drain chunk resolves — no drain listeners are
   * bound yet and a discarded document never replays — so the mount-time
   * `resource_view` (the funnel DENOMINATOR) would be lost, undercounting
   * non-converting views and inflating conversion. These lightweight
   * listeners transmit the queue directly via `sendBeacon`, but ONLY when
   * transmission is clearly permitted at that instant, mirroring the drain's
   * flush decision table with NO defer path (a pagehide continuation may
   * never run):
   *
   *   - consent not granted → DROP (clear — never transmit, and never leave
   *     entries for a later replay under a re-granted consent; the epoch
   *     already invalidates them, clearing is the drop semantics).
   *   - region KNOWN → SEND: the middleware cookie decides, or the hydrated
   *     consent-required signal (granted at this point ⇒ explicit opt-in),
   *     or a hydration pass that CONFIRMED rest-of-world geo.
   *   - region UNKNOWN (hydration pending, failed, or never ran) → HOLD:
   *     nothing ever transmits with the region unresolved. The loss
   *     hierarchy is explicit — losing a bounce view is acceptable; leaking
   *     unresolved-region or unconsented samples is not. (This is stricter
   *     at pagehide than the drain's settled-fail-open path: with no defer
   *     available, "couldn't confirm the region" means no transmission.)
   *     The queue is KEPT so a page that survives (tab switch back, bfcache
   *     restore) still replays through the drain's full gate + defer
   *     machinery once the chunk binds.
   *
   * Same payload shape as the drain (the ingest route needs no changes);
   * entries are epoch-filtered (r3543582090) and the queue is CLEARED on
   * send, so a bfcache-restored page whose chunk later resolves can't replay
   * the same events (no double-send).
   */
  if (typeof window !== "undefined") {
    let earlyViewSeq = 0
    const flushPendingRumEventsEarly = (): void => {
      try {
        // Once the sink is bound the drain owns flushing (and the queue was
        // drained at bind); this path is only for the pre-bind window.
        if (rumEventSink !== null || pendingRumEvents.length === 0) return
        if (!isRumConsentGranted()) {
          pendingRumEvents.length = 0
          return
        }
        const regionKnown =
          hasRegionCookieClient() ||
          getHydratedConsentRequiredSignal() ||
          getHydratedRegionResolved()
        if (!regionKnown) return // HOLD — never transmit unresolved-region samples
        const epochNow = currentRumConsentEpoch()
        // Bounded to ~12 events: worst-case ~2.2 KiB/event (6 × 256-char
        // props) stays under the ingest route's 30-event / 32 KiB caps, and
        // a pre-bind window realistically holds a handful of mount events.
        const events = pendingRumEvents
          .splice(0)
          .filter((p) => p.epoch === epochNow)
          .slice(0, 12)
          .map((p) => {
            // Same whitelist + bounds as the drain's enqueue, so the wire
            // shape is identical to a drain beacon.
            const props: Record<string, unknown> = {}
            for (const key of FORWARDED_EVENT_PROP_KEYS) {
              const v = p.params[key]
              if (v === undefined || v === null) continue
              if (typeof v === "string") props[key] = v.slice(0, 256)
              else if (typeof v === "number" || typeof v === "boolean") {
                props[key] = v
              }
            }
            if (p.name === "resource_view") {
              earlyViewSeq += 1
              props[RUM_VIEW_INSTANCE_KEY] = `e${earlyViewSeq}`
            }
            const rawPath =
              typeof p.params.page_path === "string" ? p.params.page_path : "/"
            const noHash = rawPath.split("#")[0] ?? rawPath
            const path = (noHash.split("?")[0] ?? noHash) || "/"
            return { name: p.name, path, props }
          })
        if (events.length === 0) return
        const referrerHost = currentNavigationReferrerHost()
        if (
          typeof navigator !== "undefined" &&
          typeof navigator.sendBeacon === "function"
        ) {
          navigator.sendBeacon(
            "/api/rum/ingest",
            new Blob(
              [
                JSON.stringify({
                  events,
                  ...(referrerHost ? { referrer_host: referrerHost } : {}),
                }),
              ],
              { type: "application/json" },
            ),
          )
        }
      } catch {
        // Telemetry must never break unload.
      }
    }
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") flushPendingRumEventsEarly()
    })
    window.addEventListener("pagehide", flushPendingRumEventsEarly)
  }
}

// Rely on NEXT_PUBLIC_* only: `CI` is not available in the client bundle for gating behavior.
const isGaExplicitlyDisabledForCi =
  process.env.NEXT_PUBLIC_DISABLE_GA_IN_CI === "true"

// Validate GA configuration on module load (skip noisy logs in CI perf workflows)
if (!GA_MEASUREMENT_ID && !isGaExplicitlyDisabledForCi) {
  console.warn("[gtag] GA_MEASUREMENT_ID is not set. Analytics will be disabled.")
}

/**
 * Whether analytics tracking is allowed (consent gate).
 *
 * Back-compat wrapper around the centralized consent module
 * (`~/lib/analytics/consent`), which now owns the cache and its event-driven
 * invalidation (cross-tab `storage` event + a same-tab consent-changed event —
 * no poller). Kept as a named export because call sites and tests import it.
 *
 * @returns true if analytics tracking should proceed
 */
export function isGaAnalyticsTrackingAllowed(): boolean {
  return isAnalyticsAllowed()
}

/**
 * Normalizes GA event parameter values for Vercel `track()` (string, number, or boolean only).
 * Objects and other unsupported values are omitted so mirroring matches GA primitives.
 *
 * @param {unknown} value - Raw GA parameter value.
 * @returns {string | number | boolean | undefined} Value for Vercel or undefined to omit.
 */
function toVercelPropertyValue(value: unknown): string | number | boolean | undefined {
  if (value === null || value === undefined) return undefined
  if (typeof value === 'string') return value
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'boolean') return value
  return undefined
}

/**
 * Builds a Vercel-safe property map from GA-style parameters.
 *
 * @param {Record<string, unknown>} params - Arbitrary key-value pairs.
 * @returns {Record<string, string | number | boolean>} Properties for `track()`.
 */
function vercelPropertiesFromParams(
  params: Record<string, unknown>
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {}
  for (const [key, raw] of Object.entries(params)) {
    const v = toVercelPropertyValue(raw)
    if (v !== undefined) {
      out[key] = v
    }
  }
  return out
}

/**
 * Mirrors a GA event to Vercel Analytics when the registry declares a Vercel
 * target for it. The mapping that used to live in a hand-maintained
 * `VERCEL_EVENT_BY_GA_ACTION` map now comes from `~/lib/analytics/registry`
 * (`vercelEventFor`), so there is one source of truth and no double-send.
 *
 * @param {string} gaAction - GA `sendEvent` action name.
 * @param {Record<string, unknown>} eventParams - Flat params passed to `gtag('event', ...)`.
 * @returns {void}
 */
function mirrorGaEventToVercel(gaAction: string, eventParams: Record<string, unknown>): void {
  const vercelName = vercelEventFor(gaAction)
  if (!vercelName || typeof window === 'undefined') {
    return
  }

  // When the Vercel event name differs from the GA action (e.g. several
  // navigation events collapse to 'navigation'), keep the GA action as a
  // property so the Vercel event stays disambiguable.
  const merged: Record<string, unknown> =
    vercelName === gaAction
      ? { ...eventParams }
      : { ...eventParams, ga_event_action: gaAction }

  try {
    vercelTrack(vercelName, vercelPropertiesFromParams(merged))
  } catch {
    // Analytics must never break UX
  }
}

/** GA4 clamps param values at 100 chars — keep labels within the same bound. */
const MAX_EVENT_LABEL_LEN = 100

/**
 * Reads a non-empty string param from an event params bag.
 *
 * @param {Record<string, unknown> | undefined} params - Event params.
 * @param {string} key - Param key.
 * @returns {string | undefined} Trimmed string or undefined.
 */
function strParam(params: Record<string, unknown> | undefined, key: string): string | undefined {
  const raw = params?.[key]
  if (typeof raw !== 'string') return undefined
  const trimmed = raw.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

/**
 * Reads a finite numeric param from an event params bag.
 *
 * @param {Record<string, unknown> | undefined} params - Event params.
 * @param {string} key - Param key.
 * @returns {number | undefined} Finite number or undefined.
 */
function numParam(params: Record<string, unknown> | undefined, key: string): number | undefined {
  const raw = params?.[key]
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : undefined
}

/**
 * Truncates a label to GA4's safe value length.
 *
 * @param {string} value - Raw label text.
 * @returns {string} Truncated label.
 */
function truncateEventLabel(value: string): string {
  if (value.length <= MAX_EVENT_LABEL_LEN) return value
  return `${value.slice(0, MAX_EVENT_LABEL_LEN - 1)}…`
}

/**
 * Builds a human-readable `event_label` when a call site omits one explicitly.
 * High-cardinality values stay in typed params (per the registry); this fills
 * the GA4 `event_label` dimension so default mobile reports and legacy UA-style
 * explorations remain usable without re-deriving labels from unregistered custom
 * dimensions.
 *
 * @param {string} action - GA4 event name.
 * @param {Record<string, unknown>} [params] - Flat event params (pre-merge).
 * @returns {string | undefined} Derived label, or undefined when nothing fits.
 */
export function deriveEventLabel(
  action: string,
  params?: Record<string, unknown>,
): string | undefined {
  const p = params ?? {}

  switch (action) {
    case 'page_view':
      return truncateEventLabel(strParam(p, 'page_title') ?? strParam(p, 'page_path') ?? 'page')

    case 'resource_view':
    case 'resource_category_view':
    case 'resource_complete': {
      const type = strParam(p, 'resource_type') ?? 'resource'
      return truncateEventLabel(type)
    }

    case 'resource_interact': {
      const type = strParam(p, 'resource_type') ?? 'resource'
      const interaction = strParam(p, 'interaction_type')
      return truncateEventLabel(interaction ? `${type}:${interaction}` : type)
    }

    case 'resource_tab_switch': {
      const fromTab = strParam(p, 'from_tab')
      const toTab = strParam(p, 'to_tab')
      if (fromTab && toTab) return truncateEventLabel(`${fromTab} → ${toTab}`)
      return undefined
    }

    case 'faq_interact':
      return truncateEventLabel(strParam(p, 'interaction_type') ?? 'faq_toggle')

    case 'request_access_start':
      return 'start'

    case 'request_access_step_view':
    case 'request_access_step_complete': {
      const stepId = strParam(p, 'step_id')
      const stepNum = numParam(p, 'step_number')
      if (stepId) return truncateEventLabel(`step_${stepNum ?? '?'}:${stepId}`)
      if (stepNum !== undefined) return `step_${stepNum}`
      const title = strParam(p, 'step_title')
      if (title) return truncateEventLabel(title)
      return undefined
    }

    case 'request_access_submit':
      return truncateEventLabel(strParam(p, 'plan_tier') ?? 'submit')

    case 'request_access_error':
      return truncateEventLabel(strParam(p, 'error_type') ?? 'error')

    case 'view_item':
    case 'begin_checkout':
    case 'purchase':
    case 'checkout_error':
    case 'framework_download':
      return truncateEventLabel(strParam(p, 'product') ?? action)

    case 'exception': {
      const component = strParam(p, 'component')
      const description = strParam(p, 'description')
      if (component) return truncateEventLabel(component)
      if (description) return truncateEventLabel(description.slice(0, 80))
      return 'exception'
    }

    case 'page_not_found':
      return truncateEventLabel(strParam(p, 'page_path') ?? '404')

    case 'consent_banner_shown':
      return truncateEventLabel(strParam(p, 'surface') ?? 'banner')

    case 'consent_banner_interaction':
      return truncateEventLabel(strParam(p, 'action') ?? 'interaction')

    case 'consent_update': {
      if (p.analytics_consent === true) return 'analytics_granted'
      if (p.analytics_consent === false) return 'analytics_denied'
      return 'consent_update'
    }

    case 'reload_attempt':
      return truncateEventLabel(strParam(p, 'topic') ?? (p.success === false ? 'failed' : 'success'))

    case 'site_navigation': {
      const fromSection = strParam(p, 'from_section')
      const toSection = strParam(p, 'to_section')
      if (fromSection && toSection) return truncateEventLabel(`${fromSection} → ${toSection}`)
      return undefined
    }

    case 'button_click': {
      const placement = strParam(p, 'placement')
      const bridge = strParam(p, 'bridge_target')
      const planTier = strParam(p, 'plan_tier')
      if (placement && planTier) return truncateEventLabel(`${placement}:${planTier}`)
      if (placement && bridge) return truncateEventLabel(`${placement}:${bridge}`)
      if (placement) {
        const destination = strParam(p, 'destination_path')
        return truncateEventLabel(destination ? `${placement}:${destination}` : placement)
      }
      {
        const fallback = strParam(p, 'destination_path') ?? strParam(p, 'element_id')
        return fallback ? truncateEventLabel(fallback) : undefined
      }
    }

    case 'scroll_depth': {
      const pct = numParam(p, 'scroll_depth_pct')
      return pct !== undefined ? `Scroll Depth ${pct}%` : undefined
    }

    default: {
      const searchTerm = strParam(p, 'search_term')
      if (searchTerm) return truncateEventLabel(searchTerm)
      const type = strParam(p, 'resource_type')
      if (type) return truncateEventLabel(type)
      return undefined
    }
  }
}

/**
 * Event parameters for GA4 tracking
 */
interface GTagEvent {
  /** Event action name */
  action: string
  /** Event category (legacy UA parameter, sent as custom param) */
  category?: string
  /** Event label (legacy UA parameter, sent as custom param) */
  label?: string
  /** Numeric value associated with event */
  value?: number
  /** Page location override */
  page_location?: string
  /** Element ID that triggered the event */
  element_id?: string
  /** Engagement time in milliseconds */
  engagement_time_msec?: number
  /** GA4 transport hint for unload-sensitive sends (`beacon` survives pagehide). */
  transport_type?: "beacon" | "image" | "xhr"
  /** Additional custom parameters */
  params?: Record<string, unknown>
}

/**
 * Send one typed event through sink-specific collection postures.
 *
 * Vercel's registry-mapped custom events remain always-on cookieless first-party
 * measurement. GA4 and the durable first-party RUM copy retain their existing
 * consent/region gates.
 *
 * @param event - Event parameters to track
 * @returns {void}
 */
export const sendEvent = (event: GTagEvent): void => {
  if (typeof window === 'undefined') {
    return
  }

  const {
    action,
    category,
    label,
    value,
    page_location,
    element_id,
    engagement_time_msec,
    params,
    transport_type,
  } = event

  // Dev-time guard: every emitted event should be declared in the registry so
  // the taxonomy can't drift. Warn (never throw, never in production).
  if (process.env.NODE_ENV !== 'production' && !isRegisteredEvent(action)) {
    console.warn(
      `[gtag] Unregistered analytics event "${action}". Declare it in ` +
        `src/lib/analytics/registry.ts (see docs/analytics/event-taxonomy.md).`
    )
  }

  // Resolve the event_category: for a REGISTERED event the registry is
  // authoritative (a call-site `category` can't drift it outside the closed
  // set); an explicit `category` is only used as a fallback for unregistered
  // (legacy/ad-hoc) events.
  const resolvedCategory = categoryFor(action) ?? category
  const mergedParams = params ?? {}
  const resolvedLabel = label ?? deriveEventLabel(action, mergedParams)

  // Active experiment dimensions ride on EVERY event after exposure
  // (docs/analytics/experiments.md) so GA4/Vercel goal events segment by
  // variant without per-call-site wiring. Passing the action lets the reader
  // drop a persisted assignment from its own OFF-SURFACE guardrail events —
  // the GA4 fallback rollup counts guardrails from these dimensions, so an
  // unscoped union skewed hold/promote (#233 `r3517282439`).
  const experimentParams = readActiveExperimentDimensions(action)

  // Build event parameters
  const eventParams: Record<string, unknown> = {
    ...experimentParams,
    ...(typeof value === 'number' ? { value } : {}),

    // GA4 standard parameters. Sanitize the page_location — default OR
    // caller-supplied — so NO event leaks sensitive query params (e.g. a Stripe
    // session_id). A caller value still "wins" (it's used instead of the
    // default), it's just stripped too; an already-clean value (e.g. the
    // storefront's safePageLocation, or a non-URL label) is returned unchanged.
    page_location: stripSensitiveParams(page_location ?? window.location.href),
    ...(element_id ? { element_id } : {}),

    // Only report engagement time when it was actually measured. (Previously a
    // synthetic 1000ms default was stamped on EVERY event, which inflates GA4
    // engagement-time and engaged-session metrics.)
    ...(typeof engagement_time_msec === 'number' && engagement_time_msec > 0
      ? { engagement_time_msec: Math.round(engagement_time_msec) }
      : {}),

    // Additional custom parameters
    ...mergedParams,

    // Derived label + registry category win over stray params keys (e.g. a
    // call site accidentally passing `event_label` inside `params`).
    ...(resolvedLabel ? { event_label: resolvedLabel } : {}),
    ...(resolvedCategory ? { event_category: resolvedCategory } : {}),
    ...(transport_type ? { transport_type } : {}),
  }

  // Vercel custom events share the always-on posture of Vercel Web Analytics
  // and Speed Insights. The registry still controls which events are mirrored.
  mirrorGaEventToVercel(action, eventParams)

  // GA4 and our durable RUM database copy retain the app-level opt-out gate.
  if (!isAnalyticsAllowed()) return

  if (window.gtag && GA_MEASUREMENT_ID) {
    window.gtag('event', action, eventParams)
  }

  // Third sink: buffer to the first-party RUM drain. Statically eliminated
  // when NEXT_PUBLIC_RUM_ENABLED isn't 'true' (see the module-scope lazy sink
  // binding — r3542898935); when enabled it's an O(1) enqueue with the batched
  // sendBeacon flush off the hot path. Wrapped so it can never break UX.
  try {
    if (rumEventSink) {
      rumEventSink(action, eventParams)
    } else if (process.env.NEXT_PUBLIC_RUM_ENABLED === "true") {
      // Sink not bound yet (cold load) — hold the event for replay-on-bind
      // (r3543138824). Dead code in disabled builds.
      if (pendingRumEvents.length >= MAX_PENDING_RUM_EVENTS) {
        pendingRumEvents.shift()
      }
      // Snapshot the pathname AT QUEUE TIME (r3543377458): the drain derives
      // an event's route from `page_path` ?? current location, and replay
      // happens after the chunk resolves — a navigation in between would
      // attribute the cold-load view to the DESTINATION route (the same
      // late-finalization class as the RumReporter mount-time capture). An
      // explicitly-passed page_path still wins via spread order. The consent
      // epoch is snapshotted the same way (r3543582090): replay only
      // re-delivers entries whose epoch is still current.
      pendingRumEvents.push({
        name: action,
        params: {
          page_path:
            typeof window !== "undefined" ? window.location.pathname : "/",
          ...eventParams,
        },
        epoch: currentRumConsentEpoch(),
      })
    }
  } catch {
    // Analytics must never break UX
  }

  // Fan-out session engagement counters for known signal events.
  if (typeof window !== "undefined") {
    const signal = engagementSignalFor(action)
    if (signal) {
      const scrollPct =
        action === "scroll_depth" && typeof params?.scroll_depth_pct === "number"
          ? params.scroll_depth_pct
          : undefined
      if (scrollPct !== undefined) {
        updateSessionMaxScroll(scrollPct)
      }
      recordEngagementSignal({
        weight: signal.weight,
        highIntent: signal.highIntent,
        countsAsAction: signal.countsAsAction !== false,
        path: window.location.pathname,
      })
    }
  }

  maybeLogExperimentConversion(action, eventParams)
}

/**
 * Best-effort Postgres ledger write when a tracked event is an experiment
 * goal metric.
 *
 * @param {string} action - GA4 event name.
 * @param {Record<string, unknown>} _eventParams - Emitted parameters (includes experiment dims).
 * @returns {void}
 */
function maybeLogExperimentConversion(
  action: string,
  _eventParams: Record<string, unknown>,
): void {
  if (typeof window === "undefined") {
    return
  }
  if (!CONVERSION_METRICS.has(action)) {
    return
  }

  // Resolve targets and the surface path SYNCHRONOUSLY at event time — the
  // async consent gate below must not race an SPA navigation into recording
  // the wrong page.
  const surfacePath = window.location.pathname
  const targets = resolveConversionTargets(action, surfacePath)
  if (targets.length === 0) {
    return
  }

  // Visitor-scoped Postgres rows are first-party analytics STORAGE: like the
  // exposure beacon, they need the stricter storage gate — `sendEvent` itself
  // is only gated on `isAnalyticsAllowed()`, which stays true pre-opt-in in
  // consent-required regions for Consent Mode's cookieless GA pings (same
  // class as #233 `r3515451592` on the exposure path). The REGION-AWARE async
  // variant is required here: when middleware didn't stamp the region cookie,
  // the sync gate would default a pre-consent EEA/UK/CH visitor to
  // rest-of-world and write an unpurgeable ledger row (mirrors
  // `recordAttributionTouchAsync` from the Consent Mode v2 reach fix, #479).
  // GA/Vercel capture is untouched: this gates ONLY the ledger row.
  void (async () => {
    try {
      if (!(await isAnalyticsStorageAllowedAsync())) {
        return
      }
    } catch {
      return
    }

    // MISSING token: full page load off the bootstrap surface (e.g. landing
    // directly on /request-access after a prior `/` exposure) — the module
    // store reset, and the guard REJECTS tokenless POSTs whenever
    // SESSION_COOKIE_SECRET is set (#233 review — bug `21ac78f1`). STALE
    // token: an SPA tab open past the 24h TTL — the guard 403s the expired
    // credential just as silently (#233 `r3517282446`). Both re-fetch via the
    // store (deduped, cheap pure read) and fall back to the best available.
    const ingestToken = await readFreshLedgerIngestToken()

    for (const target of targets) {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      }
      if (ingestToken) {
        headers[LEDGER_INGEST_TOKEN_HEADER] = ingestToken
      }

      void fetch("/api/experiments/conversion", {
        method: "POST",
        headers,
        body: JSON.stringify({
          experiment_id: target.experiment_id,
          variant_id: target.variant_id,
          metric: action,
          surface_path: surfacePath,
        }),
        keepalive: true,
      }).catch(() => {
        // Ledger is supplementary to GA4/Vercel.
      })
    }
  })()
}

/**
 * Typed event emitter — the preferred entry point for new code.
 *
 * Enforces each event's property shape from the registry
 * (`AnalyticsEventParams`) at compile time, then routes through `sendEvent`
 * (single consent gate + GA4 + registry-driven Vercel mirror). See
 * `docs/analytics/event-taxonomy.md`.
 *
 * @param name - A registered event name.
 * @param params - The event's typed properties.
 * @param options.engagementMs - Measured engagement time, surfaced as GA4 `engagement_time_msec`.
 * @param options.pageLocation - Page URL override for the event.
 * @param options.transportType - GA4 transport hint (`beacon` for pagehide/unload sends).
 */
export function track<K extends AnalyticsEventName>(
  name: K,
  params: AnalyticsEventParams[K],
  options?: { engagementMs?: number; pageLocation?: string; transportType?: "beacon" | "image" | "xhr" }
): void {
  sendEvent({
    action: name,
    page_location: options?.pageLocation,
    engagement_time_msec: options?.engagementMs,
    transport_type: options?.transportType,
    params: params as Record<string, EventPropValue>,
  })
}

/**
 * Track a client exception (GA4 recommended `exception` event). Consent-gated
 * like any other event. `description` is truncated to keep the param bounded.
 *
 * @param options.description - Error message / description.
 * @param options.fatal - Whether the error was fatal (a render crash).
 * @param options.component - Optional component/boundary that caught it.
 * @param options.errorId - Optional error id / Next.js `digest`.
 */
export function trackException(options: {
  description: string
  fatal?: boolean
  component?: string
  errorId?: string
}): void {
  track("exception", {
    description: (options.description || "unknown").slice(0, 500),
    fatal: options.fatal ?? false,
    ...(options.component ? { component: options.component } : {}),
    ...(options.errorId ? { error_id: options.errorId } : {}),
  })
}

/**
 * Record a consent change (GA4 `consent_update` event). Unlike every other
 * helper this is NOT gated by `isAnalyticsAllowed()` — the event records the
 * consent change ITSELF (including an opt-out), so it must fire regardless; the
 * Google tag's own Consent Mode state (set via `gtag('consent','update', …)` by
 * the caller) governs whether it's a cookieless ping. Typed + registry-
 * categorized so it's consistent with the rest of the layer (replaces a
 * hand-rolled `gtag('event','consent_update', …)`).
 */
export function trackConsentUpdate(params: AnalyticsEventParams["consent_update"]): void {
  if (typeof window === "undefined" || !window.gtag || !GA_MEASUREMENT_ID) return
  const category = categoryFor("consent_update")
  const derivedLabel = deriveEventLabel("consent_update", params as Record<string, unknown>)
  window.gtag("event", "consent_update", {
    ...(category ? { event_category: category } : {}),
    ...(derivedLabel ? { event_label: derivedLabel } : {}),
    ...params,
  })
}

type PrivacyUiEventName = "consent_banner_shown" | "consent_banner_interaction"

/**
 * Record a consent-banner UI event. Like {@link trackConsentUpdate}, this is NOT
 * gated by `isAnalyticsAllowed()` — the banner can show (and be interacted with)
 * while analytics is denied (EEA default-deny, GPC, explicit opt-out). Consent
 * Mode governs whether Google stores a cookie for the ping.
 *
 * @param {PrivacyUiEventName} name - Banner event name.
 * @param {AnalyticsEventParams[PrivacyUiEventName]} params - Typed event params.
 */
export function trackPrivacyUiEvent<K extends PrivacyUiEventName>(
  name: K,
  params: AnalyticsEventParams[K],
): void {
  if (typeof window === "undefined" || !window.gtag || !GA_MEASUREMENT_ID) return
  const category = categoryFor(name)
  const derivedLabel = deriveEventLabel(name, params as Record<string, unknown>)
  window.gtag("event", name, {
    ...(category ? { event_category: category } : {}),
    ...(derivedLabel ? { event_label: derivedLabel } : {}),
    page_location: stripSensitiveParams(window.location.href),
    ...params,
  })
}

/**
 * Whether `window.gtag` is defined and able to queue events.
 *
 * The `gtag` stub is now defined SYNCHRONOUSLY by the Consent Mode bootstrap in
 * `<head>` (see `~/lib/analytics/consent-mode`), so `window.gtag` is a function
 * before hydration — `isGtagReady()` is effectively true from first paint, and
 * the network library (gtag.js, `afterInteractive`) only needs to drain the
 * `dataLayer` queue later. (`sendEvent` still silently drops GA events if the
 * stub is somehow absent — e.g. analytics disabled for the environment.) Callers
 * that fire a once-ever event (e.g. the purchase beacon, which persists a dedupe
 * flag) check this so the event isn't lost; with the synchronous stub that check
 * now passes immediately rather than waiting out a `lazyOnload` race.
 *
 * @returns true when a `gtag('event', …)` call would be queued, not dropped.
 */
export function isGtagReady(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.gtag === 'function' &&
    Boolean(GA_MEASUREMENT_ID)
  )
}

/** Whether `input` is a URL/path (vs a semantic GA4 page_location label). */
function isUrlLikeForAnalyticsSanitization(input: string): boolean {
  return /^(\/|https?:\/\/)/i.test(input)
}

/**
 * Drop sensitive query params from an analytics URL (e.g. the Stripe
 * `session_id` on `/checkout/success`) before it reaches GA4, while keeping the
 * rest of the query. Preserves the input shape (relative path+search vs. absolute
 * href). Returns semantic labels (e.g. `"Homepage"`, `"Email Form"`) unchanged.
 * Returns the input unchanged if parsing fails or nothing was stripped.
 */
export function stripSensitiveParams(input: string): string {
  if (typeof window === 'undefined') return input
  if (!isUrlLikeForAnalyticsSanitization(input)) return input
  try {
    const isAbsolute = /^[a-z]+:\/\//i.test(input)
    const baseOrigin = typeof window !== 'undefined' ? window.location.origin : 'https://www.adsights.ai'
    const normalized = normalizeAnalyticsUrl(input, baseOrigin)
    const u = new URL(normalized, baseOrigin)
    let changed = normalized !== input
    for (const key of [...u.searchParams.keys()]) {
      if (SENSITIVE_QUERY_PARAM_RE.test(key)) {
        u.searchParams.delete(key)
        changed = true
      }
    }
    if (!changed) return input
    return isAbsolute ? u.href : `${u.pathname}${u.search}${u.hash}`
  } catch {
    return input
  }
}

/**
 * Track page views with consent checking
 * Only tracks if user has consented to analytics
 *
 * @param url - Page URL to track (pathname + search)
 */
export const pageview = (url: string): void => {
  if (typeof window === 'undefined' || !GA_MEASUREMENT_ID) {
    return
  }

  // GA4 is configured with send_page_view:false (see the Consent Mode
  // bootstrap), so we emit exactly one explicit page_view event per SPA
  // navigation. This is the recommended App Router pattern and removes the
  // duplicate pageview that re-running gtag('config') on every route change
  // produced. Routed through `track`/`sendEvent` (not a raw gtag call) so the
  // registry's `event_category: "page"` + consent gate apply to the
  // highest-volume event too. Note: GA4 Enhanced Measurement's "page changes
  // based on browser history events" must stay OFF, or pageviews double-count.
  //
  // Strip sensitive query params (e.g. the Stripe `session_id` on
  // /checkout/success) from BOTH page_path and page_location so they never reach
  // GA4 — same hardening the commerce helpers apply via safePageLocation.
  const pathOnly = url.split("?")[0] ?? url
  const journey = recordJourneyPage(pathOnly)
  const engagement = getSessionEngagementProps(pathOnly)

  track('page_view', {
    page_path: stripSensitiveParams(url),
    page_location: stripSensitiveParams(window.location.href),
    ...(typeof document !== 'undefined' && document.title
      ? { page_title: document.title }
      : {}),
    ...getClientContextProps(),
    ...journey,
    ...engagement,
  })
}

/**
 * Records page-level engagement when the visitor leaves a page (visibility hidden).
 * Includes measured time-on-page and max scroll depth for content quality analysis.
 *
 * @param {Object} options - Engagement measurements for the page being left.
 * @param {string} options.pagePath - Pathname (+ optional search) being exited.
 * @param {number} options.timeOnPageMs - Milliseconds since the page became visible.
 * @param {number} options.maxScrollDepthPct - Highest scroll milestone reached [0–100].
 * @returns {void}
 */
export function trackPageEngagement(options: {
  pagePath: string
  /** Full URL of the page being exited — frozen at mount for SPA navigations. */
  pageLocation?: string
  timeOnPageMs: number
  maxScrollDepthPct: number
  /** When true, depth is viewport-fit only — reported on exit but excluded from session tier scroll scoring. */
  syntheticScrollDepth?: boolean
  /** Use GA4 beacon transport (recommended for `pagehide` / tab close). */
  beacon?: boolean
}): void {
  const pathOnly = options.pagePath.split("?")[0] ?? options.pagePath
  if (!options.syntheticScrollDepth) {
    updateSessionMaxScroll(options.maxScrollDepthPct)
  }
  const peeked = peekJourneyProps(pathOnly)
  const engagement = getSessionEngagementProps(pathOnly)
  const pageLocation =
    options.pageLocation ??
    (typeof window !== "undefined" ? window.location.href : undefined)

  track(
    "page_engagement",
    {
      page_path: stripSensitiveParams(options.pagePath),
      site_section: peeked?.site_section ?? sectionForPath(pathOnly),
      page_intent: peeked?.page_intent ?? intentForPath(pathOnly),
      session_page_index: peeked?.session_page_index ?? 1,
      time_on_page_msec: Math.max(0, Math.round(options.timeOnPageMs)),
      max_scroll_depth_pct: Math.min(100, Math.max(0, Math.round(options.maxScrollDepthPct))),
      ...(options.syntheticScrollDepth ? { synthetic_scroll_depth: true } : {}),
      session_action_count: engagement.session_action_count,
      session_engagement_tier: engagement.session_engagement_tier,
    },
    {
      pageLocation,
      ...(options.beacon ? { transportType: "beacon" as const } : {}),
    },
  )
}

/**
 * Emits a terminal `session_engagement` summary for the tab session.
 *
 * @param {string} path - Current pathname.
 * @param {Object} [options] - Emitter options.
 * @param {boolean} [options.beacon=false] - Use GA4 beacon transport for pagehide sends.
 * @returns {void}
 */
export function trackSessionEngagement(
  path: string,
  options?: { beacon?: boolean },
): void {
  if (hasSessionEngagementBeenEmitted()) return
  const summary = getSessionEngagementSummary(path)
  if (!summary) return
  markSessionEngagementEmitted()
  const journey = peekJourneyProps(path)

  track(
    "session_engagement",
    {
      session_page_index: journey?.session_page_index ?? 1,
      session_action_count: summary.session_action_count,
      session_high_intent_actions: summary.session_high_intent_actions,
      session_active_time_msec: summary.session_active_time_msec,
      session_engagement_tier: summary.session_engagement_tier,
      session_max_intent: journey?.session_max_intent ?? "awareness",
      session_section_count: journey?.session_section_count ?? 1,
    },
    options?.beacon ? { transportType: "beacon" } : undefined,
  )
}

/** Parameters for {@link trackSiteAction}. */
export type SiteActionTrackParams = Readonly<{
  kind: SiteActionKind
  label: SiteActionLabel
  surface: SiteActionSurface
  element_text?: string
  destination?: string
  high_intent?: boolean
}>

/**
 * Emits a unified `site_action` event with journey and engagement context.
 *
 * @param {SiteActionTrackParams} params - Action metadata.
 * @param {Object} [options] - Emitter options.
 * @param {number} [options.engagementMs] - Time on page before the action.
 * @param {string} [options.sourcePath] - Pathname at click time (before SPA navigation).
 * @returns {void}
 */
export function trackSiteAction(
  params: SiteActionTrackParams,
  options?: { engagementMs?: number; sourcePath?: string },
): void {
  if (typeof window === "undefined") return

  const path = options?.sourcePath ?? window.location.pathname
  const label = params.label
  const highIntent = params.high_intent ?? isHighIntentActionLabel(label)
  recordSiteAction({ highIntent, path })

  const journey = peekJourneyProps(path)
  const engagement = getSessionEngagementProps(path)

  track(
    "site_action",
    {
      action_kind: params.kind,
      action_label: label,
      action_surface: params.surface,
      high_intent_action: highIntent,
      ...(params.element_text ? { element_text: params.element_text.slice(0, 80) } : {}),
      ...(params.destination ? { destination: params.destination.slice(0, 120) } : {}),
      ...(journey
        ? {
            site_section: journey.site_section,
            page_intent: journey.page_intent,
            session_page_index: journey.session_page_index,
          }
        : {}),
      session_action_count: engagement.session_action_count,
      session_engagement_tier: engagement.session_engagement_tier,
    },
    { engagementMs: options?.engagementMs },
  )
}

/**
 * Create an engagement timer for measuring total interaction time
 * for long-lived interactions (scroll, video, forms, etc.)
 * Returns a function that provides milliseconds since timer creation
 * 
 * @returns Function that returns total elapsed milliseconds
 */
export function createEngagementTimer(): () => number {
  const startTime = typeof performance !== 'undefined' ? performance.now() : Date.now()
  
  return (): number => {
    const currentTime = typeof performance !== 'undefined' ? performance.now() : Date.now()
    return Math.max(Math.round(currentTime - startTime), 1)
  }
}

/**
 * Create a stopwatch variant for measuring time between interactions
 * Returns a function that provides milliseconds since last tick (delta), not
 * since start
 * 
 * @returns Function that returns delta milliseconds since last call
 */
export function createEngagementStopwatch(): () => number {
  let lastTime = typeof performance !== 'undefined' ? performance.now() : Date.now()
  
  return (): number => {
    const currentTime = typeof performance !== 'undefined' ? performance.now() : Date.now()
    const delta = Math.max(Math.round(currentTime - lastTime), 1)
    lastTime = currentTime
    return delta
  }
}

/**
 * Create both total and delta timing functions
 * Convenience function for components that need both measurements
 * 
 * Returns a tuple of [getTotalMs, getDeltaMs].
 * - getTotalMs(): time since creation
 * - getDeltaMs(): time since last call
 * 
 * @returns Tuple of [getTotalMs, getDeltaMs] functions
 */
export function createEngagementTimers(): [() => number, () => number] {
  const getTotalMs = createEngagementTimer()
  const getDeltaMs = createEngagementStopwatch()
  return [getTotalMs, getDeltaMs]
}

/**
 * Predefined event types for consistent naming
 */
export const enum EventTypes {
  SIGNUP_ATTEMPT = "signup_attempt",
  SIGNUP_SUCCESS = "signup_success", 
  BUTTON_CLICK = "button_click"
}

/**
 * Track signup-related events with standard parameters
 * 
 * @param eventType - Type of signup event
 */
export const sendSignupEvent = (eventType: EventTypes): void => {
  sendEvent({
    action: eventType,
    label: eventType,
    element_id: "signup_form"
  })
}

/**
 * Track button click events with location context
 * 
 * @param buttonName - Name/ID of the button clicked
 * @param location - Page or section where button was clicked
 */
export const sendButtonClickEvent = (buttonName: string, location: string): void => {
  trackSiteAction({
    kind: location.toLowerCase().includes("nav") ? "navigation" : "cta",
    label: normalizeActionLabel(buttonName),
    surface: surfaceFromLocation(location),
    element_text: buttonName,
  })
}

/**
 * Track scroll depth milestones
 * 
 * @param scrollDepth - Percentage of page scrolled (0-100)
 * @param engagementMs - Optional engagement time at this depth
 */
export const trackScrollDepth = (scrollDepth: number, engagementMs?: number): void => {
  sendEvent({
    action: "scroll_depth",
    label: `Scroll Depth ${scrollDepth}%`,
    value: scrollDepth,
    engagement_time_msec: engagementMs,
    params: { scroll_depth_pct: scrollDepth }
  })
}

/**
 * Resource types for consistent categorization
 */
type ResourceType = 'hub' | 'category' | 'tool' | 'template' | 'guide' | 'calculator' | 'quiz' | 'glossary'

/**
 * Resource interaction types
 */
type ResourceAction = 'view' | 'interact' | 'complete'

/**
 * Send resource-specific events with standardized naming
 * 
 * @param {'view'|'interact'|'complete'} action - Event action category.
 * @param {Object} options - Event details.
 *   @param {'hub'|'category'|'tool'|'template'|'guide'|'calculator'|'quiz'|'glossary'} options.resourceType - Resource type.
 *   @param {string} [options.resourceSlug] - Optional resource slug.
 *   @param {Object} [options.params] - Additional GA params (e.g., interaction_type, outcome metrics).
 *   @param {number} [options.engagementMs] - Engagement ms to report.
 */
export function sendResourceEvent(
  action: ResourceAction,
  options: {
    resourceType: ResourceType
    resourceSlug?: string
    params?: Record<string, unknown>
    engagementMs?: number
  }
): void {
  const { resourceType, resourceSlug, params, engagementMs } = options
  
  const eventName = `resource_${action}` as const
  
  sendEvent({
    action: eventName,
    engagement_time_msec: engagementMs,
    params: {
      resource_type: resourceType,
      ...(resourceSlug ? { resource_slug: resourceSlug } : {}),
      ...(params ?? {})
    }
  })
}

//=============================================================================
// Enhanced Resource Interaction Tracking
//=============================================================================

/**
 * Tracks tab switching interactions within resources (e.g., glossary term tabs)
 * 
 * @param {Object} options - Tab switching event details
 *   @param {string} options.resourceType - The type of resource containing tabs
 *   @param {string} [options.resourceSlug] - The specific resource slug
 *   @param {string} options.fromTab - The tab being switched from
 *   @param {string} options.toTab - The tab being switched to
 *   @param {number} [options.engagementMs] - Time spent on previous tab
 *   @param {string} [options.elementId] - Optional element ID for the tab
 */
export function trackTabSwitch(options: {
  resourceType: string
  resourceSlug?: string
  fromTab: string
  toTab: string
  engagementMs?: number
  elementId?: string
}): void {
  const { resourceType, resourceSlug, fromTab, toTab, engagementMs, elementId } = options
  
  sendEvent({
    action: 'resource_tab_switch',
    label: `${fromTab} → ${toTab}`,
    engagement_time_msec: engagementMs,
    element_id: elementId ?? `${resourceSlug}-tabs`,
    params: {
      resource_type: resourceType,
      ...(resourceSlug ? { resource_slug: resourceSlug } : {}),
      from_tab: fromTab,
      to_tab: toTab,
      interaction_type: 'tab_switch'
    }
  })
}

/**
 * Tracks clicks on external or internal resource links
 * 
 * @param {Object} options - Link click event details
 *   @param {string} options.linkType - 'external' or 'internal'
 *   @param {string} options.linkUrl - The URL being clicked
 *   @param {string} [options.linkTitle] - The title/text of the link
 *   @param {string} [options.sourceResource] - The resource containing the link
 *   @param {string} [options.sourceResourceType] - Type of the source resource
 *   @param {string} [options.linkCategory] - Category/type of the linked resource
 *   @param {number} [options.engagementMs] - Time spent before clicking
 *   @param {string} [options.elementId] - Optional element ID for the link
 */
export function trackResourceLinkClick(options: {
  linkType: 'external' | 'internal'
  linkUrl: string
  linkTitle?: string
  sourceResource?: string
  sourceResourceType?: string
  linkCategory?: string
  engagementMs?: number
  elementId?: string
}): void {
  const { 
    linkType, 
    linkUrl, 
    linkTitle, 
    sourceResource, 
    sourceResourceType,
    linkCategory,
    engagementMs, 
    elementId 
  } = options
  
  // Parse domain from URL for better categorization
  let linkDomain = ''
  try {
    const url = new URL(linkUrl)
    linkDomain = url.hostname.replace('www.', '')
  } catch {
    // If URL parsing fails, use the first part of the URL or fallback to the full URL
    linkDomain = linkUrl.split('/')[0] ?? linkUrl
  }
  
  sendEvent({
    action: 'resource_link_click',
    label: linkTitle ?? linkUrl,
    engagement_time_msec: engagementMs,
    element_id: elementId,
    params: {
      link_type: linkType,
      link_url: linkUrl,
      link_domain: linkDomain,
      ...(linkTitle ? { link_title: linkTitle } : {}),
      ...(sourceResource ? { source_resource: sourceResource } : {}),
      ...(sourceResourceType ? { source_resource_type: sourceResourceType } : {}),
      ...(linkCategory ? { link_category: linkCategory } : {}),
      interaction_type: 'link_click'
    }
  })
}



/**
 * Tracks navigation between sections within a guide or resource
 * 
 * @param {Object} options - Section navigation event details
 *   @param {string} options.resourceType - Type of resource being navigated
 *   @param {string} options.resourceSlug - The resource being navigated
 *   @param {string} options.fromSection - Section being navigated from
 *   @param {string} options.toSection - Section being navigated to
 *   @param {string} [options.navigationMethod] - How navigation occurred ('click', 'scroll', 'anchor')
 *   @param {number} [options.engagementMs] - Time spent in previous section
 *   @param {string} [options.elementId] - Optional element ID
 */
export function trackSectionNavigation(options: {
  resourceType: string
  resourceSlug: string
  fromSection: string
  toSection: string
  navigationMethod?: string
  engagementMs?: number
  elementId?: string
}): void {
  const { 
    resourceType, 
    resourceSlug, 
    fromSection, 
    toSection, 
    navigationMethod = 'click',
    engagementMs, 
    elementId 
  } = options
  
  sendEvent({
    action: 'resource_section_navigation',
    label: `${fromSection} → ${toSection}`,
    engagement_time_msec: engagementMs,
    element_id: elementId ?? `${resourceSlug}-nav-${toSection.toLowerCase().replace(/\s+/g, '-')}`,
    params: {
      resource_type: resourceType,
      resource_slug: resourceSlug,
      from_section: fromSection,
      to_section: toSection,
      navigation_method: navigationMethod,
      interaction_type: 'section_navigation'
    }
  })
}

/**
 * Tracks download or file access events
 * 
 * @param {Object} options - Download event details
 *   @param {string} options.fileName - Name of the file being downloaded
 *   @param {string} options.fileType - Type/extension of the file
 *   @param {string} options.downloadUrl - URL of the download
 *   @param {string} [options.sourceResource] - Resource containing the download link
 *   @param {string} [options.sourceResourceType] - Type of the source resource
 *   @param {number} [options.engagementMs] - Time spent before download
 *   @param {string} [options.elementId] - Optional element ID
 */
export function trackDownload(options: {
  fileName: string
  fileType: string
  downloadUrl: string
  sourceResource?: string
  sourceResourceType?: string
  engagementMs?: number
  elementId?: string
}): void {
  const { 
    fileName, 
    fileType, 
    downloadUrl, 
    sourceResource, 
    sourceResourceType,
    engagementMs, 
    elementId 
  } = options
  
  sendEvent({
    action: 'resource_download',
    label: fileName,
    engagement_time_msec: engagementMs,
    element_id: elementId,
    params: {
      file_name: fileName,
      file_type: fileType,
      download_url: downloadUrl,
      ...(sourceResource ? { source_resource: sourceResource } : {}),
      ...(sourceResourceType ? { source_resource_type: sourceResourceType } : {}),
      interaction_type: 'download'
    }
  })
}

//=============================================================================
// Enhanced Tool Interaction Tracking
//=============================================================================

/**
 * Tracks email capture events in tools (calculators, quizzes)
 * 
 * @param {Object} options - Email capture event details
 *   @param {string} options.toolType - The type of tool (calculator, quiz, analyzer, generator)
 *   @param {string} options.toolSlug - The specific tool slug
 *   @param {boolean} [options.emailProvided] - Whether user actually provided email
 *   @param {number} [options.engagementMs] - Time spent before email capture
 */
export function trackToolEmailCapture(options: {
  toolType: string
  toolSlug: string
  emailProvided?: boolean
  engagementMs?: number
}): void {
  const { toolType, toolSlug, emailProvided, engagementMs } = options
  
  sendResourceEvent('interact', {
    resourceType: toolType as 'calculator' | 'quiz',
    resourceSlug: toolSlug,
    engagementMs,
    params: {
      interaction_type: 'email_capture',
      email_provided: emailProvided ?? false
    }
  })
}

/**
 * Tracks result sharing or copying actions in tools
 * 
 * @param {Object} options - Share/copy event details
 *   @param {string} options.toolType - The type of tool
 *   @param {string} options.toolSlug - The specific tool slug
 *   @param {string} options.shareMethod - How the result was shared (copy, url, email, social)
 *   @param {number} [options.engagementMs] - Time spent before sharing
 */
export function trackToolShare(options: {
  toolType: string
  toolSlug: string
  shareMethod: 'copy' | 'url' | 'email' | 'social' | 'download'
  engagementMs?: number
}): void {
  const { toolType, toolSlug, shareMethod, engagementMs } = options
  
  sendResourceEvent('interact', {
    resourceType: toolType as 'calculator' | 'quiz',
    resourceSlug: toolSlug,
    engagementMs,
    params: {
      interaction_type: 'share',
      share_method: shareMethod
    }
  })
}

//=============================================================================
// Search & Discovery Analytics
//=============================================================================

/**
 * Tracks search queries and their effectiveness
 * 
 * @param {Object} options - Search event details
 *   @param {string} options.searchQuery - The search query entered
 *   @param {string} options.searchType - Type of search (blog, resources, glossary, site — the cross-site palette)
 *   @param {number} options.resultCount - Number of results returned
 *   @param {string} [options.sourceLocation] - Where the search was initiated from
 *   @param {number} [options.engagementMs] - Time spent before search
 */
export function trackSearch(options: {
  searchQuery: string
  searchType: 'blog' | 'resources' | 'glossary' | 'site'
  resultCount: number
  sourceLocation?: string
  engagementMs?: number
}): void {
  const { searchQuery, searchType, resultCount, sourceLocation, engagementMs } = options
  
  sendEvent({
    action: 'search',
    label: searchQuery,
    engagement_time_msec: engagementMs,
    params: {
      search_term: searchQuery,
      search_type: searchType,
      result_count: resultCount,
      ...(sourceLocation ? { source_location: sourceLocation } : {}),
      interaction_type: 'search'
    }
  })
}

/**
 * Tracks filter usage and combinations
 * 
 * @param {Object} options - Filter event details
 *   @param {string} options.contentType - What's being filtered (blog, quizzes, guides, glossary)
 *   @param {string} options.filterType - Type of filter applied
 *   @param {string} options.filterValue - Value selected for the filter
 *   @param {number} options.resultCount - Number of results after filtering
 *   @param {string[]} [options.activeFilters] - All currently active filters
 *   @param {number} [options.engagementMs] - Time spent before filtering
 */
export function trackFilter(options: {
  contentType: 'blog' | 'quizzes' | 'guides' | 'glossary'
  filterType: string
  filterValue: string
  resultCount: number
  activeFilters?: string[]
  engagementMs?: number
}): void {
  const { contentType, filterType, filterValue, resultCount, activeFilters, engagementMs } = options
  
  sendEvent({
    action: 'filter_content',
    label: `${filterType}: ${filterValue}`,
    engagement_time_msec: engagementMs,
    params: {
      content_type: contentType,
      filter_type: filterType,
      filter_value: filterValue,
      result_count: resultCount,
      active_filter_count: activeFilters?.length ?? 1,
      ...(activeFilters ? { active_filters: activeFilters.join(',') } : {}),
      interaction_type: 'filter'
    }
  })
}

/**
 * Tracks search result clicks to measure search effectiveness
 * 
 * @param {Object} options - Search result click details
 *   @param {string} options.searchQuery - The original search query
 *   @param {string} options.resultTitle - Title of clicked result
 *   @param {string} options.resultType - Type of result (blog_post, tool, glossary_term, etc.)
 *   @param {number} options.resultPosition - Position in search results (1-based)
 *   @param {number} [options.engagementMs] - Time spent viewing results before click
 */
export function trackSearchResultClick(options: {
  searchQuery: string
  resultTitle: string
  resultType: string
  resultPosition: number
  engagementMs?: number
}): void {
  const { searchQuery, resultTitle, resultType, resultPosition, engagementMs } = options
  
  sendEvent({
    action: 'search_result_click',
    label: resultTitle,
    engagement_time_msec: engagementMs,
    params: {
      search_term: searchQuery,
      result_type: resultType,
      result_position: resultPosition,
      result_title: resultTitle,
      interaction_type: 'search_result_click'
    }
  })
}

/**
 * Tracks navigation between different sections/categories
 * 
 * @param {Object} options - Navigation event details
 *   @param {string} options.fromSection - Section being navigated from
 *   @param {string} options.toSection - Section being navigated to
 *   @param {string} options.navigationMethod - How navigation occurred
 *   @param {string} [options.fromResource] - Specific resource being navigated from
 *   @param {number} [options.engagementMs] - Time spent in previous section
 */
export function trackNavigation(options: {
  fromSection: string
  toSection: string
  navigationMethod: 'main_nav' | 'footer' | 'breadcrumb' | 'cta' | 'related_link'
  fromResource?: string
  engagementMs?: number
}): void {
  const { fromSection, toSection, navigationMethod, fromResource, engagementMs } = options
  
  sendEvent({
    action: 'site_navigation',
    label: `${fromSection} → ${toSection}`,
    engagement_time_msec: engagementMs,
    params: {
      from_section: fromSection,
      to_section: toSection,
      navigation_method: navigationMethod,
      ...(fromResource ? { from_resource: fromResource } : {}),
      interaction_type: 'navigation'
    }
  })
}

//=============================================================================
// Site-Level User Journey Analytics
//=============================================================================

/**
 * Tracks email capture across different site contexts
 * 
 * @param {Object} options - Email capture event details
 *   @param {string} options.captureContext - Where email was captured
 *   @param {string} [options.sourcePage] - Page where capture occurred
 *   @param {string} [options.sourceElement] - Specific element that triggered capture
 *   @param {boolean} [options.isReturnUser] - Whether user has engaged before
 *   @param {number} [options.engagementMs] - Time spent before email capture
 */
export function trackEmailCapture(options: {
  captureContext: 'homepage_hero' | 'tool_result' | 'blog_newsletter' | 'resource_download' | 'footer_signup' | 'request_access_form'
  sourcePage?: string
  sourceElement?: string
  isReturnUser?: boolean
  engagementMs?: number
}): void {
  const { captureContext, sourcePage, sourceElement, isReturnUser, engagementMs } = options
  
  sendEvent({
    action: 'email_capture',
    label: captureContext,
    engagement_time_msec: engagementMs,
    params: {
      capture_context: captureContext,
      ...(sourcePage ? { source_page: sourcePage } : {}),
      ...(sourceElement ? { source_element: sourceElement } : {}),
      is_return_user: isReturnUser ?? false,
      interaction_type: 'email_capture'
    }
  })
}

/** Where a marketing-site CTA was shown (hero, pricing tier, framework callout). */
export type MarketingCtaPlacement =
  | 'homepage_hero'
  | 'pricing_tier'
  | 'homepage_framework_callout'
  | 'pricing_framework_block'

/**
 * Records a click on a marketing-site CTA (hero, pricing tier, framework callout).
 *
 * @param {Object} options - CTA click context.
 * @param {MarketingCtaPlacement} options.placement - UI surface.
 * @param {string} options.destinationPath - Internal path navigated to.
 * @param {string} [options.linkText] - Visible link/button label.
 * @param {string} [options.planTier] - SaaS plan name when the CTA is on /pricing.
 * @param {number} [options.engagementMs] - Time on page before click.
 */
export function trackMarketingCtaClick(options: {
  placement: MarketingCtaPlacement
  destinationPath: string
  linkText?: string
  planTier?: string
  engagementMs?: number
}): void {
  const { placement, destinationPath, linkText, planTier, engagementMs } = options

  track(
    'button_click',
    {
      placement,
      destination_path: destinationPath,
      surface_path: typeof window !== 'undefined' ? window.location.pathname : '',
      interaction_type: 'cta_click',
      ...(planTier ? { plan_tier: planTier } : {}),
      ...(linkText ? { link_text: linkText } : {}),
    },
    { engagementMs },
  )
}

